import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { logger } from '../services/logger';
import type { Incident, Customer, Team, ConfigurationItemDto, AlertEpisode } from '../types';

interface SearchResult {
  type:
    | 'incident'
    | 'episode'
    | 'customer'
    | 'configuration-item'
    | 'team'
    | 'user'
    | 'role'
    | 'sla-policy'
    | 'service'
    | 'docs';
  id: string;
  name: string;
  subtitle?: string;
  path: string;
  icon?: string;
}

interface RecentItem {
  type: string;
  id: string;
  name: string;
  path: string;
  accessedAt: number;
}

const SEARCH_HISTORY_KEY = 'cloudinc_search_history';
const RECENT_ITEMS_KEY = 'cloudinc_recent_items';
const MAX_HISTORY = 5;
const MAX_RECENT = 8;

export function GlobalSearchBar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Search history and recent items from localStorage
  const [searchHistory, setSearchHistory] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [recentItems, setRecentItems] = useState<RecentItem[]>(() => {
    try {
      const stored = localStorage.getItem(RECENT_ITEMS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [prevDebouncedQuery, setPrevDebouncedQuery] = useState(debouncedQuery);

  if (debouncedQuery !== prevDebouncedQuery) {
    setPrevDebouncedQuery(debouncedQuery);
    setExpandedCategories(new Set());
  }

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Global keyboard shortcut (G + /)
  useEffect(() => {
    let gPressed = false;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'g' || e.key === 'G') {
        gPressed = true;
        setTimeout(() => { gPressed = false; }, 500);
      } else if (gPressed && e.key === '/') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      } else if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Check user permissions (MUST be before queries that use them)
  const isSystemAdmin = user?.role === 'SystemAdmin';
  const isTenantAdmin = user?.role === 'TenantAdmin' || isSystemAdmin;

  // Fetch data for search
  const { data: incidents } = useQuery({
    queryKey: ['incidents-search', user?.tenantId, debouncedQuery],
    queryFn: () => apiClient.getIncidents(user!.tenantId, { searchTerm: debouncedQuery, pageSize: 25 }),
    enabled: !!user?.tenantId && debouncedQuery.length >= 2,
  });

  const { data: episodes } = useQuery({
    queryKey: ['episodes-search', user?.tenantId, debouncedQuery],
    queryFn: () => apiClient.getEpisodes(user!.tenantId, { page: 1, pageSize: 50, searchTerm: debouncedQuery }),
    enabled: !!user?.tenantId && debouncedQuery.length >= 2,
  });

  const { data: customers } = useQuery({
    queryKey: ['customers-search', user?.tenantId],
    queryFn: () => apiClient.getCustomers(user!.tenantId),
    enabled: !!user?.tenantId && debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const { data: teams } = useQuery({
    queryKey: ['teams-search', user?.tenantId],
    queryFn: () => apiClient.getTeams(user!.tenantId),
    enabled: !!user?.tenantId && debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const { data: configurationItems } = useQuery({
    queryKey: ['configuration-items-search', user?.tenantId, debouncedQuery],
    queryFn: () => apiClient.getConfigurationItems(user!.tenantId, { searchTerm: debouncedQuery, pageSize: 25 }),
    enabled: !!user?.tenantId && debouncedQuery.length >= 2,
  });

  // Fetch users for search (admin only)
  const { data: users } = useQuery({
    queryKey: ['users-search', user?.tenantId],
    queryFn: () => apiClient.getUsers(user!.tenantId),
    enabled: !!user?.tenantId && isTenantAdmin && debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Fetch roles for search (admin only)
  const { data: roles } = useQuery({
    queryKey: ['roles-search', user?.tenantId],
    queryFn: () => apiClient.getRoles(user!.tenantId, true), // Include permissions
    enabled: !!user?.tenantId && isTenantAdmin && debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // SLA policy search removed in v0.128.0 (Phase 14): the underlying
  // /api/tenants/{tenantId}/sla-policies endpoint returns 410 Gone. SLA
  // administration lives on the Agreements page and reaches it through the
  // Agreements results below and the docs entry '/docs/sla'.

  // Predefined services (navigation items) - filtered by user access
  // ⚠️ COPILOT INSTRUCTION: When adding new services/pages to the application,
  // add them to this list with appropriate permission checks so they appear in global search.
  // Format: { id: 'unique-id', name: 'Display Name', path: '/route/path', icon: 'emoji', permission: 'role-check' }
  const allServices = [
    // Dashboard & Overview
    { id: 'dashboard', name: 'Dashboard', path: '/dashboard', icon: '📊', permission: true },
    { id: 'events-dashboard', name: 'Events Dashboard', path: '/events-dashboard', icon: '📡', permission: true },
    { id: 'reports', name: 'Reports', path: '/reports', icon: '📈', permission: true },
    { id: 'operational-insights', name: 'Operational Insights', path: '/operational-insights', icon: '📉', permission: true },

    // Incidents
    { id: 'incidents', name: 'All Incidents', path: '/incidents/all', icon: '🎫', permission: true },

    // Event Pipeline
    { id: 'endpoints', name: 'Ingestion Endpoints', path: '/ingestion-endpoints', icon: '🔌', permission: true },
    { id: 'events', name: 'Event History', path: '/events', icon: '📡', permission: true },
    { id: 'episodes', name: 'Episodes', path: '/episodes', icon: '🔗', permission: true },

    // CMDB
    { id: 'asset-overview', name: 'Asset Overview', path: '/dashboards/assets', icon: '🗄️', permission: true },
    { id: 'cmdb-items', name: 'CI Items', path: '/cmdb/items', icon: '🧩', permission: true },
    { id: 'cmdb-classes', name: 'CI Classes', path: '/cmdb/classes', icon: '🗂️', permission: true },
    { id: 'cmdb-catalog', name: 'CI Catalog', path: '/cmdb/catalog', icon: '📋', permission: true },
    { id: 'cmdb-hierarchy', name: 'Azure Hierarchy', path: '/cmdb/azure-hierarchy', icon: '🌳', permission: true },
    { id: 'cmdb-predefined-tags', name: 'Predefined Tags', path: '/cmdb/predefined-tags', icon: '🏷️', permission: true },
    { id: 'cmdb-tag-mappings', name: 'Tag Mappings', path: '/cmdb/tag-mappings', icon: '🏷️', permission: true },

    // Other
    { id: 'tables', name: 'Tables', path: '/tables', icon: '🗂️', permission: true },
    { id: 'notifications', name: 'Notification Center', path: '/notifications', icon: '🔔', permission: true },
    { id: 'docs', name: 'Documentation', path: '/docs/getting-started', icon: '📚', permission: true },
    { id: 'signin-logs', name: 'Sign-In Logs', path: '/auth/signin-logs', icon: '🔑', permission: true },

    // Admin – Users & Security
    { id: 'users', name: 'User Management', path: '/admin/users', icon: '👤', permission: isTenantAdmin },
    { id: 'roles', name: 'Roles & Permissions', path: '/admin/roles', icon: '🔐', permission: isTenantAdmin },
    { id: 'role-assignments', name: 'Role Assignments', path: '/admin/role-assignments', icon: '🎯', permission: isTenantAdmin },

    // Admin – Tenant Configuration
    { id: 'customers', name: 'Customers', path: '/customers', icon: '👥', permission: isTenantAdmin },
    { id: 'teams', name: 'Teams', path: '/admin/teams', icon: '👥', permission: isTenantAdmin },
    { id: 'itil-config', name: 'ITIL Configuration', path: '/admin/itil-configuration', icon: '📋', permission: isTenantAdmin },
    { id: 'sla', name: 'SLA Policies', path: '/admin/itil-configuration', icon: '⏱️', permission: isTenantAdmin },
    { id: 'priority-config', name: 'Priority Matrix', path: '/admin/itil-configuration', icon: '⚡', permission: isTenantAdmin },
    { id: 'reason-config', name: 'Reason Configuration', path: '/admin/itil-configuration', icon: '📋', permission: isTenantAdmin },
    { id: 'sla-schedules', name: 'SLA Schedules', path: '/admin/sla-schedules', icon: '⏰', permission: isTenantAdmin },
    { id: 'integrations', name: 'Integrations', path: '/admin/integrations', icon: '🔗', permission: isTenantAdmin },
    { id: 'my-integrations', name: 'My Integrations', path: '/admin/integrations/configured', icon: '🧩', permission: isTenantAdmin },
    { id: 'discovery-jobs', name: 'Discovery Jobs', path: '/admin/integrations/discovery-jobs', icon: '🔍', permission: isTenantAdmin },
    { id: 'tenant-settings', name: 'Settings', path: '/settings/tenant', icon: '⚙️', permission: isTenantAdmin },
    { id: 'tenant-ai-usage', name: 'AI Usage', path: '/settings/ai-usage', icon: '🤖', permission: isTenantAdmin },

    // Admin – On-Call
    { id: 'on-call', name: 'On-Call Schedules', path: '/admin/on-call', icon: '🗓️', permission: isTenantAdmin },
    { id: 'escalation-policies', name: 'Escalation Policies', path: '/admin/escalation-policies', icon: '🪜', permission: isTenantAdmin },
    { id: 'holiday-calendars', name: 'Holiday Calendars', path: '/admin/holiday-calendars', icon: '🎉', permission: isTenantAdmin },

  ];

  // Filter services based on user permissions
  const services = allServices.filter((service) => service.permission);

  // Documentation items - accessible to all authenticated users
  // ⚠️ COPILOT INSTRUCTION: When adding new documentation pages,
  // add them here so they appear in global search results.
  const docs = [
    { id: 'getting-started', name: 'Getting Started Guide', path: '/docs/getting-started', icon: '📖', permission: true },
    { id: 'user-guide', name: 'User Guide', path: '/docs/user-guide', icon: '📘', permission: true },
    { id: 'incident-management', name: 'Incident Management', path: '/docs/incidents', icon: '📚', permission: true },
    { id: 'incident-fields', name: 'Incident Field Reference', path: '/docs/incidents/fields', icon: '🧾', permission: true },
    { id: 'permissions-model', name: 'Permissions Model', path: '/docs/permissions', icon: '🔐', permission: true },
    { id: 'sla-policies', name: 'SLA Policies', path: '/docs/sla', icon: '⏱️', permission: true },
    { id: 'sla-fields', name: 'SLA Field Reference', path: '/docs/sla/fields', icon: '⏲️', permission: true },
    { id: 'customers-guide', name: 'Customers & Customer Users', path: '/docs/customers', icon: '🏢', permission: true },
    { id: 'customer-users', name: 'Customer Users', path: '/docs/customers/users', icon: '👤', permission: true },
    { id: 'teams-guide', name: 'Teams Guide', path: '/docs/teams', icon: '👥', permission: true },
    { id: 'users-guide', name: 'Tenant & User Management', path: '/docs/users', icon: '🛡️', permission: true },
    { id: 'tenant-users', name: 'Tenant Users', path: '/docs/users/tenant-users', icon: '🧑', permission: true },
    { id: 'settings-guide', name: 'Settings Guide', path: '/docs/settings', icon: '⚙️', permission: true },
    { id: 'tenant-settings-guide', name: 'Tenant Settings Guide', path: '/docs/settings/tenant', icon: '🏢', permission: true },
    { id: 'integrations-overview', name: 'Integrations Overview', path: '/docs/integrations', icon: '🔗', permission: true },
    { id: 'teams-setup', name: 'Microsoft Teams Setup', path: '/docs/integrations/teams', icon: '🟦', permission: true },
    { id: 'slack-setup', name: 'Slack Setup', path: '/docs/integrations/slack', icon: '💬', permission: true },
    { id: 'api-reference', name: 'API Reference', path: '/docs/api', icon: '🔗', permission: true },
    { id: 'customer-api-guide', name: 'Customer API Guide', path: '/docs/api/customer-guide', icon: '🧩', permission: true },
    { id: 'system-documentation', name: 'System Documentation', path: '/docs/system/overview', icon: '🛠️', permission: isSystemAdmin },
  ];

  // Build search results
  const searchResults: SearchResult[] = [];

  if (debouncedQuery.length >= 2) {
    const query = debouncedQuery.toLowerCase();

    // Search incidents
    incidents?.incidents.forEach((incident: Incident) => {
      if (
        incident.number?.toLowerCase().includes(query) || incident.title?.toLowerCase().includes(query) || incident.description?.toLowerCase().includes(query)
      ) {
        searchResults.push({
          type: 'incident',
          id: incident.id,
          name: `${incident.number}: ${incident.title}`,
          subtitle: incident.customerName,
          path: `/incidents/${incident.id}`,
          icon: '🎫',
        });
      }
    });

    // Search episodes
    episodes?.episodes.forEach((episode: AlertEpisode) => {
      if (
        episode.title.toLowerCase().includes(query) ||
        episode.description.toLowerCase().includes(query) ||
        episode.correlationKey.toLowerCase().includes(query) ||
        episode.id.toLowerCase().includes(query)
      ) {
        searchResults.push({
          type: 'episode',
          id: episode.id,
          name: episode.title,
          subtitle: episode.status,
          path: `/episodes/${episode.id}`,
          icon: '🔗',
        });
      }
    });

    // Search customers
    customers?.forEach((customer: Customer) => {
      if (
        customer.name.toLowerCase().includes(query) ||
        customer.code.toLowerCase().includes(query)
      ) {
        searchResults.push({
          type: 'customer',
          id: customer.id,
          name: customer.name,
          subtitle: customer.code,
          path: `/customers/${customer.id}`,
          icon: '🏢',
        });
      }
    });

    // Search configuration items
    configurationItems?.items.forEach((ci: ConfigurationItemDto) => {
      if (
        ci.name.toLowerCase().includes(query) ||
        ci.externalId?.toLowerCase().includes(query)
      ) {
        searchResults.push({
          type: 'configuration-item',
          id: ci.id,
          name: ci.name,
          subtitle: `${ci.ciClassName || 'Unknown class'} • ${ci.customerName || 'Unknown customer'}`,
          path: `/cmdb/items/${ci.id}`,
        });
      }
    });

    // Search teams
    teams?.forEach((team: Team) => {
      if (team.name.toLowerCase().includes(query)) {
        searchResults.push({
          type: 'team',
          id: team.id,
          name: team.name,
          subtitle: `${team.memberCount} members`,
          path: `/admin/teams`,
          icon: '👥',
        });
      }
    });

    // Search users (admin only)
    if (isTenantAdmin && users) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      users.forEach((user: any) => {
        if (
          user.email?.toLowerCase().includes(query) ||
          user.name?.toLowerCase().includes(query) ||
          user.firstName?.toLowerCase().includes(query) ||
          user.lastName?.toLowerCase().includes(query) ||
          `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim().toLowerCase().includes(query)
        ) {
          searchResults.push({
            type: 'user',
            id: user.id,
            name: user.email || user.name,
            subtitle: user.role || 'User',
            path: `/admin/users`,
            icon: '👤',
          });
        }
      });
    }

    // Search roles (admin only)
    if (isTenantAdmin && roles) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      roles.forEach((role: any) => {
        if (role.name?.toLowerCase().includes(query)) {
          searchResults.push({
            type: 'role',
            id: role.id,
            name: role.name,
            subtitle: `${role.permissions?.length || 0} permissions`,
            path: `/admin/roles`,
            icon: '🔐',
          });
        }
      });
    }

    // Search SLA policies removed in v0.128.0 (Phase 14); see ADR-042.

    // Search services
    services.forEach((service) => {
      if (service.name.toLowerCase().includes(query)) {
        searchResults.push({
          type: 'service',
          id: service.id,
          name: service.name,
          path: service.path,
          icon: service.icon,
        });
      }
    });

    // Search documentation
    docs.forEach((doc) => {
      if (!doc.permission) return;
      if (doc.name.toLowerCase().includes(query)) {
        searchResults.push({
          type: 'docs',
          id: doc.id,
          name: doc.name,
          path: doc.path,
          icon: doc.icon,
        });
      }
    });
  }

  const isExpanded = (category: string) => expandedCategories.has(category);
  const toggleExpand = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Group results by type
  const groupedResults = {
    incidents: searchResults.filter((r) => r.type === 'incident').slice(0, isExpanded('incidents') ? undefined : 10),
    episodes: searchResults.filter((r) => r.type === 'episode').slice(0, isExpanded('episodes') ? undefined : 10),
    customers: searchResults.filter((r) => r.type === 'customer').slice(0, isExpanded('customers') ? undefined : 10),
    configurationItems: searchResults.filter((r) => r.type === 'configuration-item').slice(0, isExpanded('configurationItems') ? undefined : 10),
    teams: searchResults.filter((r) => r.type === 'team').slice(0, isExpanded('teams') ? undefined : 10),
    users: searchResults.filter((r) => r.type === 'user').slice(0, isExpanded('users') ? undefined : 10),
    roles: searchResults.filter((r) => r.type === 'role').slice(0, isExpanded('roles') ? undefined : 10),
    slaPolicies: searchResults.filter((r) => r.type === 'sla-policy').slice(0, isExpanded('slaPolicies') ? undefined : 10),
    services: searchResults.filter((r) => r.type === 'service').slice(0, isExpanded('services') ? undefined : 10),
    docs: searchResults.filter((r) => r.type === 'docs').slice(0, isExpanded('docs') ? undefined : 10),
  };

  const totalCounts = {
    incidents: searchResults.filter((r) => r.type === 'incident').length,
    episodes: searchResults.filter((r) => r.type === 'episode').length,
    customers: searchResults.filter((r) => r.type === 'customer').length,
    configurationItems: searchResults.filter((r) => r.type === 'configuration-item').length,
    teams: searchResults.filter((r) => r.type === 'team').length,
    users: searchResults.filter((r) => r.type === 'user').length,
    roles: searchResults.filter((r) => r.type === 'role').length,
    slaPolicies: searchResults.filter((r) => r.type === 'sla-policy').length,
    services: searchResults.filter((r) => r.type === 'service').length,
    docs: searchResults.filter((r) => r.type === 'docs').length,
  };

  // Get all results in flat list for keyboard navigation
  const allResults = [
    ...groupedResults.incidents,
    ...groupedResults.episodes,
    ...groupedResults.customers,
    ...groupedResults.configurationItems,
    ...groupedResults.teams,
    ...groupedResults.users,
    ...groupedResults.roles,
    ...groupedResults.slaPolicies,
    ...groupedResults.services,
    ...groupedResults.docs,
  ];

  // Recent services (from predefined list)
  const recentServices = services.slice(0, 6);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && allResults[focusedIndex]) {
        handleSelectResult(allResults[focusedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleSelectResult = (result: SearchResult) => {
    logger.logUserAction('Global search selection', {
      type: result.type,
      id: result.id,
      query: searchQuery,
    });

    // Add to search history
    if (searchQuery.trim()) {
      const newHistory = [
        searchQuery.trim(),
        ...searchHistory.filter((h) => h !== searchQuery.trim()),
      ].slice(0, MAX_HISTORY);
      setSearchHistory(newHistory);
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
    }

    // Add to recent items
    // eslint-disable-next-line react-hooks/purity
    const timestamp = Date.now();
    const newRecent: RecentItem = {
      type: result.type,
      id: result.id,
      name: result.name,
      path: result.path,
      accessedAt: timestamp,
    };
    const updatedRecent = [
      newRecent,
      ...recentItems.filter((item) => item.id !== result.id),
    ].slice(0, MAX_RECENT);
    setRecentItems(updatedRecent);
    localStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updatedRecent));

    navigate(result.path);
    setSearchQuery('');
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  };

  const handleHistoryClick = (term: string) => {
    setSearchQuery(term);
    inputRef.current?.focus();
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'incident': return 'Incident';
      case 'episode': return 'Episode';
      case 'customer': return 'Customer';
      case 'configuration-item': return 'Configuration Item';
      case 'team': return 'Team';
      case 'user': return 'User';
      case 'role': return 'Role';
      case 'sla-policy': return 'SLA Policy';
      case 'service': return 'Service';
      case 'docs': return 'Documentation';
      default: return type;
    }
  };

  return (
    <div ref={searchRef} className="relative flex-1 max-w-2xl">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-5 w-5 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          ref={inputRef}
          data-global-search-input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setFocusedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search resources, services, and docs (G + /)"
          className="block w-full pl-10 pr-12 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800"
          aria-label="Global search"
          aria-expanded={isOpen}
          aria-controls="search-dropdown"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs text-gray-400 dark:text-gray-500 pointer-events-none">
          <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-mono">
            G + /
          </kbd>
        </div>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          id="search-dropdown"
          className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 max-h-[600px] overflow-hidden flex flex-col"
        >
          <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(600px - 48px)' }}>
            {searchQuery.length < 2 ? (
              /* Idle State: History + Recent */
              <>
                {/* Search History */}
                {searchHistory.length > 0 && (
                  <div className="border-b border-gray-100 dark:border-gray-700">
                    <div className="px-4 py-2 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
                      <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Recent Searches
                      </h3>
                      <button
                        onClick={clearHistory}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 font-medium"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="py-1">
                      {searchHistory.map((term, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleHistoryClick(term)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 text-sm"
                        >
                          <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-300">{term}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Services */}
                <div className="border-b border-gray-100 dark:border-gray-700">
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                    <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Quick Access
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2 p-3">
                    {recentServices.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => handleSelectResult({ ...service, type: 'service' })}
                        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 text-left transition-colors"
                      >
                        <span className="text-xl flex-shrink-0">{service.icon}</span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                          {service.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recent Items */}
                {recentItems.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                      <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Recently Accessed
                      </h3>
                    </div>
                    <div className="py-1">
                      {recentItems.slice(0, 5).map((item) => (
                        <button
                          key={`${item.id}-${item.type}`}
                          onClick={() => navigate(item.path)}
                          className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-8 h-8 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg">
                                {item.type === 'incident' ? '🎫' : item.type === 'episode' ? '🔗' : item.type === 'customer' ? '🏢' : '👥'}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</p>
                              <p className="text-xs text-gray-500 capitalize">{getTypeLabel(item.type)}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Help text */}
                {searchHistory.length === 0 && recentItems.length === 0 && (
                  <div className="px-6 py-12 text-center text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-sm font-medium mb-1">Search CloudIncidents</p>
                    <p className="text-xs">Type to search incidents, customers, teams, services, and docs</p>
                  </div>
                )}
              </>
            ) : (
              /* Active Search: Live Results */
              <>
                {allResults.length === 0 ? (
                  <div className="px-6 py-12 text-center text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium mb-1">No results found</p>
                    <p className="text-xs">Try different keywords or check our documentation</p>
                  </div>
                ) : (
                  <>
                    {/* Incidents */}
                    {groupedResults.incidents.length > 0 && (
                      <div className="border-b border-gray-100 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            Incidents ({totalCounts.incidents})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('incidents') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.incidents.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-lg">{result.icon}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                    {result.subtitle && (
                                      <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">Incident</span>
                              </button>
                            );
                          })}
                          {totalCounts.incidents > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('incidents'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('incidents') ? 'Show fewer incidents' : `Show all ${totalCounts.incidents} incidents`}
                            >
                              {isExpanded('incidents') ? 'Show less' : `Show all ${totalCounts.incidents} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Episodes */}
                    {groupedResults.episodes.length > 0 && (
                      <div className="border-b border-gray-100 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            Episodes ({totalCounts.episodes})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('episodes') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.episodes.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-lg">{result.icon}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                    {result.subtitle && (
                                      <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">Episode</span>
                              </button>
                            );
                          })}
                          {totalCounts.episodes > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('episodes'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('episodes') ? 'Show fewer episodes' : `Show all ${totalCounts.episodes} episodes`}
                            >
                              {isExpanded('episodes') ? 'Show less' : `Show all ${totalCounts.episodes} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Customers */}
                    {groupedResults.customers.length > 0 && (
                      <div className="border-b border-gray-100 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            Customers ({totalCounts.customers})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('customers') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.customers.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-lg">{result.icon}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                    {result.subtitle && (
                                      <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">Customer</span>
                              </button>
                            );
                          })}
                          {totalCounts.customers > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('customers'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('customers') ? 'Show fewer customers' : `Show all ${totalCounts.customers} customers`}
                            >
                              {isExpanded('customers') ? 'Show less' : `Show all ${totalCounts.customers} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Configuration Items */}
                    {groupedResults.configurationItems.length > 0 && (
                      <div className="border-b border-gray-100 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            Configuration Items ({totalCounts.configurationItems})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('configurationItems') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.configurationItems.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
                                    <svg className="h-4 w-4 text-cyan-700 dark:text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 100 4h14a2 2 0 100-4M5 12a2 2 0 110-4h14a2 2 0 110 4m-9 4h4" />
                                    </svg>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                    {result.subtitle && (
                                      <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">CI</span>
                              </button>
                            );
                          })}
                          {totalCounts.configurationItems > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('configurationItems'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('configurationItems') ? 'Show fewer configuration items' : `Show all ${totalCounts.configurationItems} configuration items`}
                            >
                              {isExpanded('configurationItems') ? 'Show less' : `Show all ${totalCounts.configurationItems} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Teams */}
                    {groupedResults.teams.length > 0 && (
                      <div className="border-b border-gray-100 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            Teams ({totalCounts.teams})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('teams') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.teams.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-lg">{result.icon}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                    {result.subtitle && (
                                      <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">Team</span>
                              </button>
                            );
                          })}
                          {totalCounts.teams > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('teams'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('teams') ? 'Show fewer teams' : `Show all ${totalCounts.teams} teams`}
                            >
                              {isExpanded('teams') ? 'Show less' : `Show all ${totalCounts.teams} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Users */}
                    {groupedResults.users.length > 0 && (
                      <div className="border-b border-gray-100 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            Users ({totalCounts.users})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('users') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.users.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-lg">{result.icon}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                    {result.subtitle && (
                                      <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">User</span>
                              </button>
                            );
                          })}
                          {totalCounts.users > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('users'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('users') ? 'Show fewer users' : `Show all ${totalCounts.users} users`}
                            >
                              {isExpanded('users') ? 'Show less' : `Show all ${totalCounts.users} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Roles */}
                    {groupedResults.roles.length > 0 && (
                      <div className="border-b border-gray-100 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            Roles ({totalCounts.roles})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('roles') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.roles.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-lg">{result.icon}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                    {result.subtitle && (
                                      <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">Role</span>
                              </button>
                            );
                          })}
                          {totalCounts.roles > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('roles'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('roles') ? 'Show fewer roles' : `Show all ${totalCounts.roles} roles`}
                            >
                              {isExpanded('roles') ? 'Show less' : `Show all ${totalCounts.roles} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* SLA Policies */}
                    {groupedResults.slaPolicies.length > 0 && (
                      <div className="border-b border-gray-100 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            SLA Policies ({totalCounts.slaPolicies})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('slaPolicies') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.slaPolicies.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center flex-shrink-0 dark:bg-pink-900/30">
                                    <span className="text-lg">{result.icon}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                    {result.subtitle && (
                                      <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">SLA Policy</span>
                              </button>
                            );
                          })}
                          {totalCounts.slaPolicies > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('slaPolicies'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('slaPolicies') ? 'Show fewer SLA policies' : `Show all ${totalCounts.slaPolicies} SLA policies`}
                            >
                              {isExpanded('slaPolicies') ? 'Show less' : `Show all ${totalCounts.slaPolicies} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Services */}
                    {groupedResults.services.length > 0 && (
                      <div className="border-b border-gray-100 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            Services ({totalCounts.services})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('services') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.services.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-lg">{result.icon}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">Service</span>
                              </button>
                            );
                          })}
                          {totalCounts.services > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('services'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('services') ? 'Show fewer services' : `Show all ${totalCounts.services} services`}
                            >
                              {isExpanded('services') ? 'Show less' : `Show all ${totalCounts.services} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Documentation */}
                    {groupedResults.docs.length > 0 && (
                      <div>
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            Documentation ({totalCounts.docs})
                          </h3>
                        </div>
                        <div className={`py-1 ${isExpanded('docs') ? 'max-h-96 overflow-y-auto' : ''}`}>
                          {groupedResults.docs.map((result) => {
                            const globalIdx = allResults.indexOf(result);
                            return (
                              <button
                                key={result.id}
                                onClick={() => handleSelectResult(result)}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between transition-colors ${
                                  focusedIndex === globalIdx ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : 'border-l-4 border-transparent'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-lg">{result.icon}</span>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{result.name}</p>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">Docs</span>
                              </button>
                            );
                          })}
                          {totalCounts.docs > 10 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand('docs'); }}
                              className="w-full px-3 py-1.5 text-xs text-primary-700 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
                              aria-label={isExpanded('docs') ? 'Show fewer docs' : `Show all ${totalCounts.docs} docs`}
                            >
                              {isExpanded('docs') ? 'Show less' : `Show all ${totalCounts.docs} results`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer with keyboard shortcuts */}
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500 flex-shrink-0">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded font-mono">↑↓</kbd>
                <span>Navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded font-mono">Enter</kbd>
                <span>Select</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded font-mono">Esc</kbd>
                <span>Close</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
